import { Component } from "react";

import { Box } from "@mui/material";
import { FormattedMessage } from "react-intl";
import { connect, ConnectedProps } from "react-redux";
import { Routes, Route } from "react-router-dom";
import { bindActionCreators } from "redux";

import {
  setFetch,
  changeLanguage,
  setEagriAuth,
  registerAccountAndLogin,
  createEmptyFarmAndRedirect,
  createEagriFarmAndRedirect,
  validateFarmShapefile,
  importFarmShapefile,
} from "../../actions/signup.actions";

import {
  login,
  refreshLogin,
} from "../../../../common/services/auth/Auth.service";
import { checkEmailExists } from "../../../../shared/api/gateway/accounts/accounts.api";
import { EntryContainer } from "../../../../shared/containers/EntryContainer/EntryContainer";
import { Thunk } from "../../../../types";
import { EAgriImportNotification } from "../../components/EagriImportNotification/EAgriImportNotification";
import FarmImport from "../../components/FarmImport/FarmImport";
import NewAccount from "../../components/NewAccount/NewAccount";
import { SignupToLogin } from "../../components/SignupToLogin/SignupToLogin";

import { SignupState } from "../../../../reducers/signup.reducer.types";

type ReduxProps = ConnectedProps<typeof connector>;

type Props = ReduxProps & {
  history: { push: (path: string) => void };
  langId: string;
};

class Signup extends Component<Props> {
  onCreateAccount = (email: string, password: string) => {
    const { history } = this.props;
    const goToFarmImport = () => history.push("/signup/import");
    return this.props.registerAccountAndLogin(
      email,
      password,
      login,
      goToFarmImport,
    );
  };

  goToFarm = () => {
    this.props.history.push("/farm");
  };

  onImportShapefile = (farmname: string, country: string, file: File) =>
    this.props.importFarmShapefile(
      farmname,
      country,
      file,
      refreshLogin,
      this.goToFarm,
    );

  validateFarmShapefile = (country: string, file: File) => {
    this.props.validateFarmShapefile(country, file);
  };

  createEagriFarm = (farmName: string) => {
    const { history } = this.props;
    const goToSuccessPage = () => history.push("/signup/import-in-progress");
    const goErrorPage = () => history.push("/signup/import-failed");
    return this.props.createEagriFarmAndRedirect(
      farmName,
      goToSuccessPage,
      goErrorPage,
    );
  };

  createEmptyFarm = (countryCode: string, farmName: string) =>
    this.props.createEmptyFarmAndRedirect(
      farmName,
      countryCode,
      refreshLogin,
      this.goToFarm,
    );

  render() {
    const { farmShpValidationResult, isFetching, langId } = this.props;

    return (
      <EntryContainer isFetching={isFetching}>
        <Routes>
          <Route
            path=""
            element={
              <Box
                display="flex"
                flexDirection="column"
                gap={2}
                justifyContent="center"
              >
                <NewAccount
                  checkEmailExists={this.props.checkEmailExists}
                  onCreateAccount={this.onCreateAccount}
                />
                <SignupToLogin />
              </Box>
            }
          />
          <Route
            path="import"
            element={
              <FarmImport
                farmShpValidationResult={farmShpValidationResult}
                langId={langId}
                onCreateEagriFarm={this.createEagriFarm}
                onEmptyFarmCreate={this.createEmptyFarm}
                onImportShapefile={this.onImportShapefile}
                onSetEagriAuthData={this.props.setEagriAuth}
                onValidateShapefile={this.validateFarmShapefile}
              />
            }
          />
          <Route
            path="import-in-progress"
            element={
              <EAgriImportNotification
                buttonCopy={
                  <FormattedMessage id="FarmImport.notification.button" />
                }
                description={
                  <FormattedMessage
                    id="FarmImport.notification.description"
                    values={{
                      str: ((chunks: React.ReactNode) => (
                        <span style={{ fontWeight: "bold" }}>{chunks}</span>
                      )) as unknown as React.ReactNode,
                    }}
                  />
                }
                heading={
                  <FormattedMessage id="FarmImport.notification.heading" />
                }
              />
            }
          />
          <Route
            path="import-failed"
            element={
              <EAgriImportNotification
                type="error"
                buttonCopy={
                  <FormattedMessage id="FarmImport.notification.error.button" />
                }
                description={
                  <FormattedMessage id="FarmImport.notification.error.description" />
                }
                heading={
                  <FormattedMessage id="FarmImport.notification.error.heading" />
                }
              />
            }
          />
        </Routes>
      </EntryContainer>
    );
  }
}

const mapStateToProps = (state: SignupState) => ({
  isFetching: state.ui.signup.isFetching,
  farmShpValidationResult: state.ui.signup.farmShpValidationResult,
});

const mapDispatchToProps = (dispatch: Thunk<SignupState>) =>
  bindActionCreators(
    {
      setFetch,
      changeLanguage,
      setEagriAuth,
      checkEmailExists,
      registerAccountAndLogin,
      createEmptyFarmAndRedirect,
      createEagriFarmAndRedirect,
      validateFarmShapefile,
      importFarmShapefile,
    },
    dispatch,
  );
const connector = connect(mapStateToProps, mapDispatchToProps);
export default connector(Signup);
